import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  Bot,
  CalendarDays,
  CircleHelp,
  FileText,
  LayoutGrid,
  Building2,
  ChartNoAxesCombined,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  MessageCircle,
  Paperclip,
  Puzzle,
  Send,
  UserRound,
  X,
} from "lucide-react";
import Nex3Logo from "@/components/site/Nex3Logo";

const API = `${process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || "https://nex-3-api.vercel.app"}/api/chat`;
const FAQ_STORAGE_KEY = "nex3-faq-chat-v4";
const POST_STORAGE_KEY = "nex3-post-chat-v2";
const SUBMISSION_STORAGE_KEY = "nex3-submission-v2";
const FAQ_GREETING = "Get answers on AI strategy, implementation, and ROI for your organization";
const MAX_MESSAGE_LENGTH = 1200;
const CONTACT_EMAIL = "nex3info@gmail.com";
const BOOKING_URL = "https://cal.com/purmehdi/30min";
// Replies where the assistant could not answer, or handed the visitor to the team.
const NEEDS_HUMAN = /nex3info@gmail\.com|proper conversation with the nex3 team|\b(i|we) (do not|don[’']t) (have|know)\b|\bnot sure\b|\b(cannot|can[’']t|unable to) (answer|help|provide|confirm|share)\b|\b(speak|talk) (with|to) (the|a|our) (nex3 )?(team|person|human)\b/i;
const needsHuman = (text) => NEEDS_HUMAN.test(text);
const UNANSWERED_MESSAGE = "I wasn't able to answer that one. You can escalate to a human on the Nex3 team below.";
const SIDEBAR_ITEMS = [
  {
    label: "Services",
    Icon: LayoutGrid,
    answer:
      "Nex3 helps leaders plan, build, and hire for the AI economy:\n\n01 · Plan — Clarity before capital. We pressure-test your thesis, map the real market, and turn ambition into a decision you can defend, in days rather than quarters.\n\n02 · Build — Ship the right thing. From architecture and AI agent design to go-to-market, we sit inside the work to help you build a product and company that hold up in the real world.\n\n03 · Hire — The team that compounds. We help you find and close the operators, engineers, and leaders who move the needle, and design the org so every next hire is easier.\n\nAsk me about any of these, or book a call to discuss your goals.",
    cta: { label: "Explore our approach", href: "/#approach" },
  },
  {
    label: "Resources",
    Icon: FileText,
    answer:
      "Here are free Nex3 resources to get started:\n\n• Free AI Budget Audit — map your AI tools and subscriptions, spot duplicated or underused spend, and receive a personalized bill summary by email.\n• The Ultimate Guide to Free AI — a practical guide to high-value AI tools you can use at no cost, sent alongside your audit results.\n\nYou can also ask me about AI agents, costs, timelines, or implementation right here.",
    cta: { label: "Explore resources", href: "/audit" },
  },
  {
    label: "Book a Call",
    Icon: CalendarDays,
    answer:
      "Ready to build smarter? Book a free 30-minute discovery call with the Nex3 team. We'll discuss your goals, current AI maturity, and where AI can create the most impact—whether you're starting with a workshop or ready for full agent deployment.\n\nPick a time that works for you using the link below.",
    cta: { label: "Book a call", href: "https://cal.com/purmehdi/30min", external: true },
  },
  {
    label: "Help",
    Icon: CircleHelp,
    answer:
      "Happy to help. Here's how to get the most out of this assistant:\n\n• Type any question about AI strategy, implementation, costs, or timelines in the box below.\n• Use Services, Resources, or Book a Call in the sidebar for quick answers.\n• Click AI Assistant to return to the popular topics.\n\nNeed a person? Send us a message through the contact form and the team will reply within 1–2 business days.",
    cta: { label: "Get help", href: "/#contact" },
  },
];
const TOPICS = [
  {
    label: "What is an AI agent?",
    Icon: Bot,
    answer:
      "An AI agent is a software system that can understand a goal, reason through the steps needed, and take useful actions with limited supervision. Nex3 helps teams identify practical agent use cases, design the right workflows, and build systems that fit the way their business actually operates.",
  },
  {
    label: "How much does it cost for an organization?",
    Icon: CircleDollarSign,
    answer:
      "The cost for an organization depends on the scope, complexity, team size, and stage of the work. After a short discovery conversation, Nex3 recommends a focused engagement with clear deliverables, timing, and cost—so your organization knows exactly what it is investing in before work begins.",
  },
  {
    label: "How to audit your AI budget?",
    Icon: ChartNoAxesCombined,
    answer:
      "Start by mapping every AI tool, model, vendor, experiment, and internal project to its owner, monthly cost, usage, business outcome, and risk. Nex3 can help identify duplicated spend, underused tools, hidden implementation costs, and high-value investments, then turn the findings into a practical AI budget and governance plan.",
    cta: {
      label: "Audit your AI budget",
      href: "https://www.nex3.xyz/audit",
    },
  },
  {
    label: "Typical timelines",
    Icon: Clock3,
    answer:
      "Most focused advisory and workshop engagements take days or a few weeks. Larger product, architecture, and implementation projects may run longer. Nex3 defines the milestones up front and keeps the work oriented around decisions and tangible outcomes.",
  },
  {
    label: "What services do you offer?",
    Icon: Puzzle,
    answer:
      "Nex3 helps leaders plan, build, and hire for the AI economy. That includes strategy, market and product validation, technical architecture, AI agent design, token and go-to-market work, workshops, and support finding the operators and specialists needed to execute.",
  },
  {
    label: "Which industries can use Nex3 services?",
    Icon: Building2,
    answer:
      "Nex3 services can support organizations across financial services, education, professional services, hospitality, retail, technology, nonprofits, and other sectors. The work is industry-aware but begins with the specific business problem, decision, or goal your organization needs to address.",
  },
];

const loadStored = (key, fallback) => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key));
    return parsed ?? fallback;
  } catch {}
  return fallback;
};

const firstName = (name) => name.trim().split(/\s+/)[0] || "";

function confirmationMessage({ name = "", company = "" }) {
  const person = firstName(name);
  const project = company.trim();
  const thanks = person ? `Thanks for reaching out, ${person}! 🎯` : "Thanks for reaching out! 🎯";
  const subject = project ? `your inquiry about ${project}` : "your inquiry";
  return `${thanks}\n\nI’ve received ${subject}. We’ll review what you’re working on and get back to you within 1–2 business days.\n\nIn the meantime, feel free to ask me anything about our services, workshops, or how we typically work with clients.`;
}

function Escalation({ onNavigate }) {
  return (
    <div className="nex3-chat-escalate" role="group" aria-label="Escalate to a human at Nex3">
      <p><UserRound aria-hidden="true" /><span>Escalate to human</span></p>
      <div className="nex3-chat-escalate-actions">
        <a href="/#contact" onClick={onNavigate}>Message the team</a>
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">Book a call</a>
        <a href={`mailto:${CONTACT_EMAIL}`}>Email us</a>
      </div>
    </div>
  );
}

const canAutoFocus = () =>
  typeof window !== "undefined" && window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;

export default function Chatbot() {
  const initial = useRef(null);
  if (!initial.current) {
    const storedSubmission = loadStored(SUBMISSION_STORAGE_KEY, null);
    initial.current = {
      mode: "faq",
      submission: storedSubmission,
      messages: loadStored(FAQ_STORAGE_KEY, [{ role: "assistant", content: FAQ_GREETING }]),
    };
  }
  const [mode, setMode] = useState(initial.current.mode);
  const [messages, setMessages] = useState(initial.current.messages);
  const [submission, setSubmission] = useState(initial.current.submission);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeNav, setActiveNav] = useState("assistant");
  const panel = useRef(null);
  const inputRef = useRef(null);
  const historyRef = useRef(null);
  const requestRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem(mode === "post" ? POST_STORAGE_KEY : FAQ_STORAGE_KEY, JSON.stringify(messages));
    if (submission) sessionStorage.setItem(SUBMISSION_STORAGE_KEY, JSON.stringify(submission));
  }, [messages, mode, submission]);

  useEffect(() => {
    const handleSubmission = ({ detail }) => {
      const nextSubmission = {
        firstName: firstName(detail?.name || ""),
        email: (detail?.email || "").trim(),
        company: (detail?.company || "").trim(),
      };
      const postGreeting = { role: "assistant", content: confirmationMessage(detail || {}) };
      sessionStorage.setItem(POST_STORAGE_KEY, JSON.stringify([postGreeting]));
      sessionStorage.setItem(SUBMISSION_STORAGE_KEY, JSON.stringify(nextSubmission));
      setSubmission(nextSubmission);
      setMode("post");
      setMessages([postGreeting]);
      setOpen(true);
    };
    window.addEventListener("nex3:inquiry-submitted", handleSubmission);
    return () => window.removeEventListener("nex3:inquiry-submitted", handleSubmission);
  }, []);

  // Lets other components (e.g. the "Ask Nex3 AI" card) open the assistant.
  useEffect(() => {
    const handleOpen = () => {
      setMode("faq");
      setMessages(loadStored(FAQ_STORAGE_KEY, [{ role: "assistant", content: FAQ_GREETING }]));
      setError("");
      setActiveNav("assistant");
      setOpen(true);
    };
    window.addEventListener("nex3:open-chat", handleOpen);
    return () => window.removeEventListener("nex3:open-chat", handleOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    // Only auto-focus on desktop; on touch devices this would pop up the keyboard.
    if (canAutoFocus()) inputRef.current?.focus();
    const handleKey = (event) => {
      if (event.key === "Escape") setOpen(false);
      if (event.key !== "Tab" || !panel.current) return;
      const focusable = [...panel.current.querySelectorAll("button:not(:disabled), input:not(:disabled), a[href]")];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop?.();
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previousOverflow;
      window.__lenis?.start?.();
    };
  }, [open]);

  useEffect(() => {
    const history = historyRef.current;
    if (!history) return;
    if (messages.length === 1 && !loading) {
      history.scrollTo({ top: 0 });
      return;
    }
    history.scrollTo({ top: history.scrollHeight, behavior: "smooth" });
  }, [messages, loading, open]);

  useEffect(() => () => requestRef.current?.abort(), []);

  const showCanned = (item) => {
    if (loading) return;
    requestRef.current?.abort();
    setError("");
    setActiveNav(item.label);
    setMessages((current) => [...current, { role: "user", content: item.label }]);
    setLoading(true);
    window.setTimeout(() => {
      setMessages((current) => [...current, { role: "assistant", content: item.answer, cta: item.cta || null }]);
      setLoading(false);
    }, 550);
  };

  const sendMessage = async (value) => {
    const text = value.trim();
    if (!text || loading) return;
    setError("");
    setInput("");
    const userMessage = { role: "user", content: text.slice(0, MAX_MESSAGE_LENGTH) };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setLoading(true);
    const topic = TOPICS.find(({ label }) => label.toLowerCase() === text.toLowerCase());
    if (topic?.answer) {
      window.setTimeout(() => {
        setMessages((current) => [
          ...current,
          { role: "assistant", content: topic.answer, cta: topic.cta || null },
        ]);
        setLoading(false);
      }, 550);
      return;
    }
    const controller = new AbortController();
    requestRef.current = controller;
    try {
      const response = await fetch(API, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({
          messages: nextMessages.slice(-10),
          submissionContext: mode === "post" ? submission : null,
        }),
        signal: controller.signal,
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || typeof body.reply !== "string") throw new Error(body.error || "Assistant unavailable");
      setMessages((current) => [...current, { role: "assistant", content: body.reply, escalate: needsHuman(body.reply) }]);
    } catch (requestError) {
      if (requestError.name !== "AbortError") setMessages((current) => [...current, { role: "assistant", content: UNANSWERED_MESSAGE, escalate: true }]);
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
      setLoading(false);
    }
  };

  const submit = (event) => {
    event.preventDefault();
    // Dismiss the on-screen keyboard after sending so the reply is visible.
    if (!canAutoFocus()) inputRef.current?.blur();
    sendMessage(input);
  };

  const openFaqWindow = () => {
    setMode("faq");
    setMessages(loadStored(FAQ_STORAGE_KEY, [{ role: "assistant", content: FAQ_GREETING }]));
    setError("");
    setActiveNav("assistant");
    setOpen(true);
  };

  const showAssistantHome = () => {
    if (loading) return;
    setActiveNav("assistant");
    setError("");
    if (mode === "faq") setMessages([{ role: "assistant", content: FAQ_GREETING }]);
  };

  const showHome = mode === "faq" && messages.length === 1;

  return (
    <aside className="nex3-chat" aria-label="Nex3 AI Assistant">
      <AnimatePresence>
        {open && (
        <motion.div
          key="nex3-chat-overlay"
          className="nex3-chat-overlay"
          data-lenis-prevent
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}
        >
        <motion.section
          ref={panel}
          className="nex3-chat-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="nex3-chat-title"
          initial={{ opacity: 0, scale: 0.9, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 8 }}
          transition={{ type: "spring", stiffness: 320, damping: 28, mass: 0.8 }}
        >
          <nav className="nex3-chat-sidebar" aria-label="Assistant navigation">
            <span className="nex3-chat-mark">
              <Nex3Logo animate={false} color="#0B0B0C" className="nex3-chat-wordmark" />
              <span>INC.</span>
            </span>
            <ul>
              <li>
                <button
                  type="button"
                  className={activeNav === "assistant" ? "is-active" : undefined}
                  aria-current={activeNav === "assistant" ? "page" : undefined}
                  onClick={showAssistantHome}
                >
                  <MessageCircle aria-hidden="true" /><span>New Chat</span>
                </button>
              </li>
              {SIDEBAR_ITEMS.map((item) => (
                <li key={item.label}>
                  <button
                    type="button"
                    className={activeNav === item.label ? "is-active" : undefined}
                    aria-current={activeNav === item.label ? "page" : undefined}
                    onClick={() => showCanned(item)}
                    disabled={loading}
                  >
                    <item.Icon aria-hidden="true" /><span>{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="nex3-chat-sidebar-foot">
              <p>AI for<br />Real Business Impact.</p>
              <small>© {new Date().getFullYear()} Nex3 Inc.</small>
            </div>
          </nav>

          <div className="nex3-chat-main">
            <h2 id="nex3-chat-title" className="sr-only">{mode === "post" ? "Nex3 Inquiry Assistant" : "Nex3 AI Assistant"}</h2>
            <button type="button" className="nex3-chat-close" onClick={() => setOpen(false)} aria-label="Close chat"><X /></button>
            <div ref={historyRef} className="nex3-chat-history" role="log" aria-live="polite" aria-relevant="additions" data-lenis-prevent>
              <div className="nex3-chat-messages">
                {messages.map((message, index) => message.role === "assistant" && index === 0 ? (
                  showHome ? null : (
                    <div key={`${message.role}-${index}`} className="nex3-chat-welcome">
                      <span className="nex3-chat-welcome-icon"><MessageCircle aria-hidden="true" /></span>
                      <div>
                        <span className="nex3-chat-speaker">{mode === "post" ? "Inquiry Assistant" : "Nex3 Assistant"}</span>
                        <p>{message.content}</p>
                      </div>
                    </div>
                  )
                ) : (
                  <div key={`${message.role}-${index}`} className={message.cta || message.escalate ? "nex3-chat-reply" : undefined}>
                    <div className={`nex3-chat-message ${message.role}`}>{message.content}</div>
                    {message.cta && (
                      <a
                        className="nex3-chat-cta"
                        href={message.cta.href}
                        aria-label={message.cta.label}
                        {...(message.cta.external
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : message.cta.href.startsWith("/#") ? { onClick: () => setOpen(false) } : {})}
                      >
                        <span>{message.cta.label}</span>
                        <ArrowRight aria-hidden="true" />
                      </a>
                    )}
                    {message.escalate && <Escalation onNavigate={() => setOpen(false)} />}
                  </div>
                ))}
                {loading && <div className="nex3-chat-message assistant nex3-chat-typing" aria-label="Assistant is typing"><i /><i /><i /></div>}
                {error && <div className="nex3-chat-error" role="alert">{error}</div>}
              </div>
              {showHome && (
                <section className="nex3-chat-topics" aria-labelledby="nex3-topic-title">
                  <h3 id="nex3-topic-title">Popular topics</h3>
                  <div className="nex3-chat-topic-grid">
                    {TOPICS.map(({ label, Icon }) => (
                      <button type="button" key={label} onClick={() => sendMessage(label)} disabled={loading}>
                        <Icon aria-hidden="true" /><span>{label}</span><ChevronRight aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </div>
            <form className="nex3-chat-form" onSubmit={submit}>
              <label htmlFor="nex3-chat-input" className="sr-only">{mode === "post" ? "Message the Nex3 inquiry assistant" : "Message the Nex3 AI assistant"}</label>
              <input id="nex3-chat-input" ref={inputRef} value={input} onChange={(event) => setInput(event.target.value)} maxLength={MAX_MESSAGE_LENGTH} disabled={loading} placeholder={mode === "post" ? "Ask about your inquiry…" : "Ask a question..."} autoComplete="off" />
              <button type="button" className="nex3-chat-attach" aria-label="File attachments are not available" title="File attachments are not available" disabled><Paperclip /></button>
              <button type="submit" className="nex3-chat-send" disabled={loading || !input.trim()} aria-label="Send message"><Send /></button>
            </form>
            <p className="nex3-chat-privacy">Responses are trained on current Nex3 methodologies.</p>
          </div>
        </motion.section>
        </motion.div>
        )}
      </AnimatePresence>
      {!open && (
        <button type="button" className="nex3-chat-launcher" onClick={openFaqWindow} aria-label="Chat with AI"><span className="nex3-chat-live" aria-hidden="true" /><MessageCircle /></button>
      )}
    </aside>
  );
}
